import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Search, HelpCircle, FileText, CheckCircle2, ShieldAlert } from "lucide-react";
import Footer from "../components/Footer";

export default function TermsPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const sections = useMemo(() => [
    {
      id: "acceptance",
      title: "1. Acceptance of Terms",
      content: "By accessing and using Nutri Saathi (\"we,\" \"our,\" or \"us\"), you agree to be bound by these Terms & Conditions. If you do not agree to these terms, please do not use the application or services. These terms form a binding legal agreement between you and Nutri Saathi.",
      highlights: ["You must agree to these terms to use Nutri Saathi.", "These terms represent a legally binding contract."]
    },
    {
      id: "eligibility",
      title: "2. Eligibility & Account Registration",
      content: "To use Nutri Saathi, you must be at least 18 years of age and possess the legal authority to enter into this agreement. If you are under 18, you may only use our services under the direct supervision of a parent or legal guardian. You agree to provide accurate, current, and complete profile information during registration (such as age, gender, height, and weight) to enable proper service customization.",
      highlights: ["Must be 18+ or supervised by a guardian.", "You agree to provide true and accurate health data."]
    },
    {
      id: "scope-services",
      title: "3. Scope of Services",
      content: "Nutri Saathi is an AI-powered fitness and nutrition assistant. Our services include: (a) generating customized Indian-style meal suggestions based on user parameters, (b) sending automated voice call updates (\"AI check-in/accountability calls\") to track check-ins, and (c) providing a 24/7 AI chat assistant for motivational support and food estimation. These services are delivered digitally over the internet.",
      highlights: ["Includes meal plans, daily AI calls, and chat support.", "Services are fully digital and subject to internet connectivity."]
    },
    {
      id: "medical-disclaimer",
      title: "4. Important Health & Medical Disclaimer",
      content: "Nutri Saathi is NOT a medical organization, hospital, clinic, or certified clinical dietician. The meal plans, macro estimates, and coaching interactions are generated by artificial intelligence models and are intended solely for general wellness and lifestyle education. They do not constitute clinical guidance, medical diagnosis, or medical treatment plans. If you have pre-existing medical conditions (such as diabetes, heart disease, high blood pressure, kidney disease, or pregnancy), you are strictly advised to consult a primary physician before initiating any diet plans suggested by our service.",
      highlights: ["Nutri Saathi is an AI, NOT a medical doctor or licensed dietician.", "Consult your doctor first if you have chronic health conditions or are pregnant."]
    },
    {
      id: "billing-refunds",
      title: "5. Subscriptions, Payments & Cancellations",
      content: "We offer subscription packages to access premium features. Payments are processed securely via Razorpay. Subscriptions are billed on a recurring monthly basis. You can cancel your subscription at any time directly through the Settings panel in your Dashboard. Upon cancellation, your subscription remains active until the end of your current billing period, after which future billing terminates. We do not offer partial refunds for mid-cycle cancellations.",
      highlights: ["Secure payments processed via Razorpay.", "Cancel anytime through Dashboard Settings.", "No partial refunds for mid-cycle cancellations."]
    },
    {
      id: "ai-liability",
      title: "6. Limitations of Liability for AI Outputs",
      content: "Because our diet suggestions and check-in calls are powered by artificial intelligence models, we cannot guarantee absolute accuracy or safety of the generated contents. You understand that nutrition advice is generalized and that errors may occur. Nutri Saathi and its developers shall not be liable for any physical or health complications, data errors, or system downtime resulting from reliance on the AI advice.",
      highlights: ["AI recommendations may contain errors.", "Users assume responsibility for following suggestions."]
    },
    {
      id: "governing-law",
      title: "7. Governing Law",
      content: "These Terms & Conditions are governed by and construed in accordance with the laws of India. Any disputes arising out of or related to these terms shall be subject to the exclusive jurisdiction of the courts located in Mumbai, Maharashtra.",
      highlights: ["Governed by the laws of India.", "Subject to exclusive jurisdiction in Mumbai."]
    }
  ], []);

  // Filter sections based on search query
  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return sections;
    const lowerQuery = searchTerm.toLowerCase();
    return sections.filter(
      (sec) =>
        sec.title.toLowerCase().includes(lowerQuery) ||
        sec.content.toLowerCase().includes(lowerQuery) ||
        sec.highlights.some((hl) => hl.toLowerCase().includes(lowerQuery))
    );
  }, [sections, searchTerm]);

  const handleScrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-background relative flex flex-col">
      {/* Background pattern */}
      <div className="fixed inset-0 opacity-[0.02] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIgnTUwMSUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIwLjA1Ii8+PC9zdmc+')]"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <img src="/logo.svg" alt="Nutri Saathi Logo" className="w-6 h-6 object-contain" />
            </div>
            <span className="font-bold text-foreground">Nutri Saathi Trust Center</span>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-12">
        {/* Page Hero */}
        <div className="text-center max-w-2xl mx-auto mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 px-3  py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
            <FileText className="w-3.5 h-3.5" />
            Legal Agreement
          </div>
          <h1 className="text-4xl font-extrabold text-foreground font-serif">Terms & Conditions</h1>
          <p className="text-muted-foreground">
            Please read these terms carefully before using Nutri Saathi. These rules ensure a safe and supportive experience for all our users.
          </p>
          <p className="text-xs text-muted-foreground">Last updated: May 2026</p>
        </div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-10 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <input
            type="text"
            placeholder="Search terms, billing, medical disclaimer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white/60 dark:bg-black/20 border border-border/80 focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Table of Contents Sidebar - Desktop only */}
          <aside className="hidden lg:block lg:col-span-1 space-y-4 sticky top-24 self-start">
            <div className="p-5 rounded-2xl bg-secondary/20 border border-border/40 space-y-4">
              <h3 className="font-bold text-foreground text-sm tracking-wider uppercase flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-primary" />
                <span>On This Page</span>
              </h3>
              <nav className="flex flex-col gap-2.5">
                {sections.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => handleScrollTo(sec.id)}
                    className="text-left text-xs font-medium text-muted-foreground hover:text-primary transition-colors focus:outline-none hover:translate-x-1 duration-150"
                  >
                    {sec.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Policy Contents */}
          <section className="col-span-1 lg:col-span-3 space-y-10">
            {filteredSections.length === 0 ? (
              <div className="text-center py-16 bg-secondary/15 rounded-3xl border border-border/40 space-y-3">
                <ShieldAlert className="w-10 h-10 text-accent mx-auto animate-bounce" />
                <p className="text-foreground font-semibold">No results found for "{searchTerm}"</p>
                <p className="text-sm text-muted-foreground">Try searching for keywords like "medical," "cancellation," or "subscription."</p>
              </div>
            ) : (
              filteredSections.map((sec) => (
                <div
                  key={sec.id}
                  id={sec.id}
                  className="p-6 md:p-8 rounded-3xl bg-card border border-border/50 shadow-sm space-y-5 fade-in scroll-mt-24 transition-all duration-300 hover:shadow-md"
                >
                  <h2 className="text-2xl font-bold text-foreground font-serif">{sec.title}</h2>
                  <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                    {sec.content}
                  </p>

                  {/* Highlights Callout Box */}
                  <div className="p-4 rounded-2xl bg-secondary/30 dark:bg-secondary/10 border border-border/30 space-y-2.5">
                    <span className="text-xs font-bold text-primary uppercase tracking-wider block">Key Takeaways</span>
                    <ul className="space-y-1.5">
                      {sec.highlights.map((hl, index) => (
                        <li key={index} className="flex items-start gap-2 text-xs text-muted-foreground leading-normal">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                          <span>{hl}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
